import React from 'react';
import { AlertTriangle, Building2, Database, User, UserCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DuplicatePatientDetails } from '@/types/api.types';
import { Messages } from '@/utils/messages';

interface DuplicatePatientDialogProps {
  details: DuplicatePatientDetails | null;
  onClose: () => void;
  onTakeover?: (cnp: string) => void;
}

export const DuplicatePatientDialog: React.FC<DuplicatePatientDialogProps> = ({
  details,
  onClose,
  onTakeover,
}) => {
  if (!details) {
    return null;
  }

  const registeredByName = details.registeredBy.name || 'Utilizator necunoscut';
  const registeredByLabel = details.registeredBy.username
    ? `${registeredByName} (${details.registeredBy.username})`
    : registeredByName;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent hideClose className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            {Messages.duplicatePatientTitle}
          </DialogTitle>
          <DialogDescription>
            {Messages.duplicatePatientDescription(details.cnp)}
          </DialogDescription>
        </DialogHeader>

        <dl className="grid gap-3 rounded-md border bg-muted/30 p-4 text-sm">
          <div className="flex items-start gap-3">
            <User className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <div>
              <dt className="text-muted-foreground">Înregistrat de</dt>
              <dd className="font-medium">{registeredByLabel}</dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <div>
              <dt className="text-muted-foreground">Spital</dt>
              <dd className="font-medium">{details.hospital.name || 'Spital necunoscut'}</dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Database className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <div>
              <dt className="text-muted-foreground">Registru</dt>
              <dd className="font-medium">{details.registry}</dd>
            </div>
          </div>
        </dl>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="mr-2 h-4 w-4" />
            {Messages.closeAction}
          </Button>
          {onTakeover && (
            <Button onClick={() => onTakeover(details.cnp)}>
              <UserCheck className="mr-2 h-4 w-4" />
              {Messages.takeoverPatientAction}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
